// ============================================================================
// CHECK-OUT — ROOM PICKER (PMS phase 1)
// ============================================================================
// Replaces QuickCheckOutModal. That modal collected a bill and flipped the whole
// booking in one action; this one departs ROOMS.
//
// Rooms of a group empty across a morning — housekeeping cannot wait for the last
// one to leave before starting on the first, and an early-departing room should
// be sellable the same night. The booking closes when the last room goes.
//
// No money is collected here. Settlement happens against the folio, and a
// booking reaching CHECKED_OUT with an open folio is the normal path for a
// party-billed stay.
//
// Follows the lifecycle-modal pattern (crs-web/CLAUDE.md): inline state, reset on
// open, single submit, no dirty tracking or minimize — this is a transition to
// complete or abandon, not a form to park.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { departureService } from 'api/services/departureService';
import { checkInService } from 'api/services/checkInService';
import { formCText, type GrcStatus } from 'api/types/checkin.types';
import { ConfirmDialog } from 'components/modals/common/ConfirmDialog';
import IssueInvoicesModal, { invoiceActions } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import notificationService from 'services/notificationService';
import type { ID } from 'api/types';
import type { Booking } from 'api/types/booking.types';
import type { DeparturePreview, StayChargeDecision, StayChargePreview } from 'api/types/departure.types';
import StayChargesPanel, { chargedAmount, isCovered, type StayChargeEdit } from './StayChargesPanel';
import { formatCurrency } from 'utils/currencyFormat';

interface CheckOutRoomPickerModalProps {
  isOpen: boolean;
  booking: Booking | null;
  onClose: () => void;
  onCheckedOut: () => void;
}

const toDateInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const toTimeInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(11, 16);
};

const fmtDepartedAt = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : `${toDateInput(d)} ${toTimeInput(d)}`;
};

const CheckOutRoomPickerModal: React.FC<CheckOutRoomPickerModalProps> = ({
  isOpen, booking, onClose, onCheckedOut,
}) => {
  const [preview, setPreview] = useState<DeparturePreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const [selected, setSelected] = useState<Set<ID>>(new Set());
  const [departureDate, setDepartureDate] = useState('');
  const [departureTime, setDepartureTime] = useState('');
  const [notes, setNotes] = useState('');
  const [override, setOverride] = useState(false);
  const [overrideReason, setOverrideReason] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  /** Set once the last room left: the stay's invoices open next (slice 7a). */
  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);
  /** Early check-in / late check-out / overstay / retention for the rooms leaving now (slice 7c-2). */
  const [stay, setStay] = useState<StayChargePreview | null>(null);
  const [stayError, setStayError] = useState<string | null>(null);
  const [stayEdits, setStayEdits] = useState<Record<string, StayChargeEdit>>({});

  /** Registration card still incomplete (GRC-3): warned before the card closes at checkout, never blocking. */
  const [grc, setGrc] = useState<GrcStatus | null>(null);
  useEffect(() => {
    if (!isOpen || !booking?.bookingId) { setGrc(null); return; }
    let cancelled = false;
    checkInService.getByBooking(booking.bookingId)
      .then((rec) => { if (!cancelled) setGrc(rec?.grc ?? null); })
      .catch(() => { if (!cancelled) setGrc(null); });
    return () => { cancelled = true; };
  }, [isOpen, booking?.bookingId]);

  // ── Load rooms on open ────────────────────────────────────────────────────
  useEffect(() => {
    if (!isOpen || !booking?.bookingId) return;
    let cancelled = false;

    const now = new Date();
    setDepartureDate(toDateInput(now));
    setDepartureTime(toTimeInput(now));
    setNotes('');
    setOverride(false);
    setOverrideReason('');
    setFieldErrors({});
    setPreview(null);
    setStay(null);
    setStayEdits({});
    setLoading(true);

    departureService.getPreview(booking.bookingId)
      .then((p) => {
        if (cancelled) return;
        setPreview(p);
        // Everyone leaving is the common case, so pre-tick every occupied room.
        // The operator un-ticks the exception rather than building the list.
        setSelected(new Set(p.rooms.filter(r => !r.departedAt).map(r => r.checkInRoomId)));
      })
      .catch(() => {
        if (!cancelled) notificationService.toast.error('Failed to load the room list.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [isOpen, booking?.bookingId]);

  // The stay charges follow the rooms, date and time chosen.
  useEffect(() => {
    if (!isOpen || !booking?.bookingId || selected.size === 0 || !departureDate || !departureTime) { setStay(null); return; }
    const handle = window.setTimeout(() => {
      departureService.previewStayCharges(booking.bookingId, {
        checkInRoomIds: Array.from(selected),
        departureDate,
        departureTime: departureTime.length === 5 ? `${departureTime}:00` : departureTime,
      })
        .then((s) => { setStay(s); setStayError(null); })
        .catch((err: any) => { setStay(null); setStayError(err?.response?.data?.message || err?.message || 'Could not work out the stay charges.'); });
    }, 300);
    return () => window.clearTimeout(handle);
  }, [isOpen, booking?.bookingId, selected, departureDate, departureTime]);

  const stayDecisions = useMemo((): StayChargeDecision[] => {
    if (!stay) return [];
    const live = stay.lines.filter((l) => !l.alreadyDone);
    return live
      .filter((l) => l.kind !== 'OVERSTAY' && !isCovered(l, live, stayEdits)
        && (l.requiresDecision || chargedAmount(l, stayEdits) !== l.proposed))
      .map((l) => ({ key: l.key, amount: chargedAmount(l, stayEdits), reason: stayEdits[l.key]?.reason?.trim() || null }));
  }, [stay, stayEdits]);

  const occupied = useMemo(
    () => (preview?.rooms ?? []).filter(r => !r.departedAt),
    [preview],
  );

  const closesBooking = occupied.length > 0 && selected.size === occupied.length;

  const isEarly = useMemo(() => {
    if (!preview?.bookedCheckOutDate || !departureDate) return false;
    return departureDate < preview.bookedCheckOutDate.slice(0, 10);
  }, [preview, departureDate]);

  const gateBlocking = !!preview?.gateEnabled && (preview?.pendingArrivalChanges ?? 0) > 0;

  const toggle = useCallback((id: ID) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const validate = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (selected.size === 0) errors.rooms = 'Select at least one room to check out.';
    if (!departureDate) errors.departureDate = 'Departure date is required.';
    if (!departureTime) errors.departureTime = 'Departure time is required.';
    if (gateBlocking && !override) {
      errors.override = 'Tick the override to check out with unpriced arrival changes.';
    }
    if (gateBlocking && override && !overrideReason.trim()) {
      errors.overrideReason = 'A reason is required to override.';
    }
    for (const d of stayDecisions) {
      const line = stay?.lines.find((l) => l.key === d.key);
      if (!line) continue;
      if (d.amount < 0 || d.amount > line.entitled) errors[d.key] = `Between 0 and ${line.entitled}.`;
      else if (d.amount < line.entitled && (d.reason ?? '').length < 3) errors[d.key] = 'Give the reason for charging less.';
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [selected, departureDate, departureTime, gateBlocking, override, overrideReason, stayDecisions, stay]);

  const submit = useCallback(async () => {
    if (!booking?.bookingId) return;
    setConfirmOpen(false);
    setSubmitting(true);
    try {
      const result = await departureService.departRooms(booking.bookingId, {
        checkInRoomIds: Array.from(selected),
        departureDate,
        departureTime: departureTime.length === 5 ? `${departureTime}:00` : departureTime,
        notes: notes.trim() || undefined,
        overrideArrivalChangeGate: override || undefined,
        overrideReason: override ? overrideReason.trim() : undefined,
        stayCharges: stayDecisions.length ? stayDecisions : undefined,
      });

      // Nights night audit had not posted are posted at departure (slice 7a-2).
      const posted = result.nightsPostedAtDeparture
        ? ` ${result.nightsPostedAtDeparture} night line(s) not yet posted by night audit were posted (${formatCurrency(result.amountPostedAtDeparture ?? 0)}).`
        : '';
      notificationService.toast.success(
        (result.roomsOccupied === 0
          ? `${booking.bookingNumber} checked out.`
          : `${result.roomsTotal - result.roomsOccupied} of ${result.roomsTotal} rooms checked out — ${result.roomsOccupied} still in-house.`) + posted,
      );
      if (result.stayChargesPosted || result.stayChargesWaived)
        notificationService.toast.info(
          `Stay charges: ${result.stayChargesPosted ?? 0} posted (${formatCurrency(result.stayChargesAmount ?? 0)})`
          + (result.stayChargesWaived ? `, ${result.stayChargesWaived} waived (${formatCurrency(result.stayChargesWaivedAmount ?? 0)})` : '')
          + (result.stayChargeVouchers?.length ? ` — ${result.stayChargeVouchers.join(', ')}` : '') + '.');
      if (result.nightPostingFailures?.length)
        notificationService.toast.warning(`Some nights could not be posted: ${result.nightPostingFailures.join('; ')}`);
      onCheckedOut();
      // The stay is over: straight on to its invoices (slice 7a), for those who may see them.
      if (result.roomsOccupied === 0 && invoiceActions().includes('view')) setInvoiceFor(String(booking.bookingId));
      else onClose();
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Check-out failed.',
      );
    } finally {
      setSubmitting(false);
    }
  }, [booking, selected, departureDate, departureTime, notes, override, overrideReason, stayDecisions,
      onCheckedOut, onClose]);

  const handleCheckOut = useCallback(() => {
    if (!validate()) return;
    // Confirm only when something needs saying — an early departure releases
    // nights back to availability, and an override is a deliberate act. A clean
    // check-out commits straight through.
    if (isEarly || override) setConfirmOpen(true);
    else submit();
  }, [validate, isEarly, override, submit]);

  if (invoiceFor) {
    return (
      <IssueInvoicesModal isOpen bookingId={invoiceFor} onClose={() => { setInvoiceFor(null); onClose(); }} />
    );
  }

  if (!isOpen || !booking) return null;

  return (
    <>
      <div className="modal-backdrop" onClick={submitting ? undefined : onClose} />
      <div className="modal-container modal-md">
        {submitting && (
          <div style={{
            position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
            display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
          }}>
            <div style={{
              width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
              borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
            }} />
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>
              Checking out...
            </span>
          </div>
        )}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">
            Check Out — {booking.bookingNumber}
            {preview?.guestName ? ` · ${preview.guestName}` : ''}
          </h2>
          <button className="modal-close" onClick={onClose} disabled={submitting}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          {loading && (
            <div style={{ padding: '1.5rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
              Loading rooms…
            </div>
          )}

          {!loading && preview && (
            <>
              {/* ── Rooms ── */}
              <div className="form-group" data-field="rooms">
                <label className="form-label">
                  Rooms <span className="text-danger">*</span>
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.5rem' }}>
                    {occupied.length} of {preview.roomsTotal} still in-house
                  </span>
                </label>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                  {preview.rooms.map((room) => {
                    const gone = !!room.departedAt;
                    const checked = selected.has(room.checkInRoomId);
                    return (
                      <div
                        key={room.checkInRoomId}
                        onClick={gone || submitting ? undefined : () => toggle(room.checkInRoomId)}
                        role={gone ? undefined : 'button'}
                        tabIndex={gone ? undefined : 0}
                        onKeyDown={(e) => {
                          if (gone || submitting) return;
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            toggle(room.checkInRoomId);
                          }
                        }}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '0.625rem',
                          padding: '0.5rem 0.625rem',
                          border: '1px solid var(--border-color)',
                          borderRadius: '5px',
                          cursor: gone ? 'default' : 'pointer',
                          userSelect: 'none',
                          opacity: gone ? 0.55 : 1,
                          background: checked ? 'rgba(37,99,235,0.06)' : 'transparent',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={gone || submitting}
                          readOnly
                          style={{ pointerEvents: 'none' }}
                        />
                        <span style={{ fontWeight: 600, minWidth: '3.5rem' }}>{room.roomNumber}</span>
                        <span style={{ color: 'var(--text-secondary)', fontSize: '0.8125rem' }}>
                          {room.roomCategoryName}
                        </span>
                        <span style={{
                          marginLeft: 'auto', fontSize: '0.75rem', color: 'var(--text-muted)',
                        }}>
                          {gone ? `departed ${fmtDepartedAt(room.departedAt!)}` : 'in-house'}
                        </span>
                      </div>
                    );
                  })}
                </div>

                {fieldErrors.rooms && <div className="form-error-message">{fieldErrors.rooms}</div>}

                <div style={{
                  marginTop: '0.5rem', fontSize: '0.75rem', color: 'var(--text-secondary)',
                }}>
                  {closesBooking
                    ? 'This closes the booking — the last occupied room is leaving.'
                    : `The booking stays checked in; ${occupied.length - selected.size} room(s) remain.`}
                </div>
              </div>

              {/* ── When ── */}
              <div className="form-grid-2">
                <div className="form-group" data-field="departureDate">
                  <label className="form-label">Departure Date <span className="text-danger">*</span></label>
                  <input
                    type="date"
                    className={`form-input${fieldErrors.departureDate ? ' form-field-error' : ''}`}
                    value={departureDate}
                    disabled={submitting}
                    // The property allows one business day back and nothing ahead (PMS slice 6b);
                    // the server checks it against the property clock.
                    min={preview?.earliestDepartureDate?.slice(0, 10)}
                    max={toDateInput(new Date())}
                    onChange={(e) => setDepartureDate(e.target.value)}
                  />
                  {fieldErrors.departureDate && (
                    <div className="form-error-message">{fieldErrors.departureDate}</div>
                  )}
                </div>
                <div className="form-group" data-field="departureTime">
                  <label className="form-label">Time <span className="text-danger">*</span></label>
                  <input
                    type="time"
                    className={`form-input${fieldErrors.departureTime ? ' form-field-error' : ''}`}
                    value={departureTime}
                    disabled={submitting}
                    onChange={(e) => setDepartureTime(e.target.value)}
                  />
                  {fieldErrors.departureTime && (
                    <div className="form-error-message">{fieldErrors.departureTime}</div>
                  )}
                </div>
              </div>

              {isEarly && (
                <div style={{
                  padding: '0.5rem 0.75rem', borderRadius: '5px', fontSize: '0.8125rem',
                  border: '1px solid var(--warning, #d97706)',
                  background: 'rgba(217,119,6,0.10)', color: 'var(--text-primary)',
                  marginBottom: '0.75rem',
                }}>
                  Early departure — booked to {preview.bookedCheckOutDate.slice(0, 10)}. The unused
                  nights return to availability for the selected room(s).
                </div>
              )}

              {grc?.pending && (
                <div style={{
                  padding: '0.5rem 0.75rem', borderRadius: '5px', marginBottom: '0.75rem', fontSize: '0.8125rem',
                  background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
                }}>
                  Registration card incomplete: {grc.missing.join(', ')}. Complete it on Edit Check-In before the last room
                  leaves; after checkout the card is closed.
                  {formCText(grc.formCDueAt) && <b> {formCText(grc.formCDueAt)!.text}.</b>}
                </div>
              )}

              {preview?.nightAuditPendingSince && (
                <div style={{
                  padding: '0.5rem 0.75rem', borderRadius: '5px', marginBottom: '0.75rem', fontSize: '0.8125rem',
                  background: 'var(--warning-bg, #fef3c7)', color: 'var(--warning-text, #92400e)',
                }}>
                  Night audit pending since {new Date(preview.nightAuditPendingSince).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}.
                  The departing rooms' nights not yet posted are posted at check-out, so the invoice has every night.
                </div>
              )}

              {/* ── Arrival-change gate ── */}
              {gateBlocking && (
                <div style={{
                  padding: '0.625rem 0.75rem', borderRadius: '5px',
                  border: '1px solid var(--danger, #dc2626)',
                  background: 'rgba(220,38,38,0.08)', marginBottom: '0.75rem',
                }}>
                  <div style={{ fontSize: '0.8125rem', marginBottom: '0.5rem' }}>
                    <strong>{preview.pendingArrivalChanges} arrival change(s) awaiting pricing.</strong>{' '}
                    The Booking Executive should settle them before the guest leaves.
                  </div>
                  <label style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    fontSize: '0.8125rem', cursor: 'pointer',
                  }} data-field="override">
                    <input
                      type="checkbox"
                      checked={override}
                      disabled={submitting}
                      onChange={(e) => setOverride(e.target.checked)}
                    />
                    Override and release the guest anyway
                  </label>
                  {fieldErrors.override && (
                    <div className="form-error-message">{fieldErrors.override}</div>
                  )}
                  {override && (
                    <div style={{ marginTop: '0.5rem' }} data-field="overrideReason">
                      <input
                        type="text"
                        className={`form-input${fieldErrors.overrideReason ? ' form-field-error' : ''}`}
                        placeholder="Reason for the override (logged)"
                        value={overrideReason}
                        disabled={submitting}
                        maxLength={200}
                        onChange={(e) => setOverrideReason(e.target.value)}
                      />
                      {fieldErrors.overrideReason && (
                        <div className="form-error-message">{fieldErrors.overrideReason}</div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {stayError && <div className="form-error-message" style={{ marginBottom: '0.5rem' }}>{stayError}</div>}
              {stay && (
                <StayChargesPanel preview={stay} edits={stayEdits} errors={fieldErrors} disabled={submitting}
                  onChange={(key, e) => setStayEdits((prev) => ({ ...prev, [key]: e }))} />
              )}

              <div className="form-group">
                <label className="form-label">Notes</label>
                <input
                  type="text"
                  className="form-input"
                  value={notes}
                  disabled={submitting}
                  maxLength={200}
                  placeholder="Optional"
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            onClick={handleCheckOut}
            disabled={submitting || loading || !preview || occupied.length === 0}
          >
            {occupied.length === 0
              ? 'All rooms departed'
              : closesBooking
                ? `Check Out · ${selected.size} room${selected.size === 1 ? '' : 's'}`
                : `Depart ${selected.size} room${selected.size === 1 ? '' : 's'} · ${occupied.length - selected.size} stay`}
          </button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        title={closesBooking ? 'Confirm Check-Out' : 'Confirm Room Departure'}
        message={[
          `${selected.size} room(s) will be checked out.`,
          isEarly ? 'This is an early departure — the unused nights return to availability.' : '',
          override ? 'You are overriding the unpriced arrival-change gate. This is logged.' : '',
          closesBooking ? 'This closes the booking.' : '',
        ].filter(Boolean).join(' ')}
        confirmLabel="Check Out"
        cancelLabel="Cancel"
        variant={override ? 'danger' : 'default'}
        onConfirm={submit}
        onCancel={() => setConfirmOpen(false)}
      />
    </>
  );
};

export default CheckOutRoomPickerModal;
